<script setup lang="ts">
  import { computed } from 'vue';
  import { Monitor } from 'lucide-vue-next';
  import { SelectInput } from '@/shared/ui';
  import RecordConfigRepeater from './RecordConfigRepeater.vue';
  import type { RecordConfigOption } from '../../api/record-config-options.api';

  const EMPTY_MEANS_MESSAGE = 'Nenhum meio de proteção vinculado à origem.';

  const props = defineProps<{
    modelValue: Array<number | null>;
    protectionMeans: RecordConfigOption[];
    isLoading: boolean;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: Array<number | null>];
  }>();

  const hasNoMeans = computed(() => props.protectionMeans.length === 0 && !props.isLoading);

  function optionsFor(index: number, ownValue: number | null): RecordConfigOption[] {
    const takenElsewhere = new Set(
      props.modelValue.filter((value, rowIndex) => rowIndex !== index && value !== null)
    );

    return props.protectionMeans.filter(({ id }) => id === ownValue || !takenElsewhere.has(id));
  }

  function toRowValue(value: string | number): number | null {
    return value === '' ? null : Number(value);
  }
</script>

<template>
  <section class="flex flex-col gap-4 px-8 py-6" data-testid="record-config-protection-mean-section">
    <span class="text-sm font-semibold text-gray-07">Meio de proteção:</span>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2 text-sm font-semibold text-gray-07">
        <Monitor class="size-4 text-[var(--theme-secondary-ink)]" aria-hidden="true" />
        <span>Sistema Eletrônico</span>
      </div>

      <p
        v-if="hasNoMeans"
        class="text-sm text-gray-05"
        data-testid="record-config-protection-mean-empty"
      >
        {{ EMPTY_MEANS_MESSAGE }}
      </p>

      <RecordConfigRepeater
        v-else
        :model-value="modelValue"
        :empty-value="null"
        add-label="Adicionar meio de proteção"
        remove-label="Remover meio de proteção"
        @update:model-value="emit('update:modelValue', $event)"
      >
        <template #default="{ value, index, update }">
          <SelectInput
            :id="`record-config-protection-mean-${index}`"
            :model-value="value ?? ''"
            :options="optionsFor(index, value)"
            label="Selecione"
            :auto-select-first="false"
            :disabled="isLoading"
            container-class="w-72 max-w-full"
            @update:model-value="update(toRowValue($event))"
          />
        </template>
      </RecordConfigRepeater>
    </div>
  </section>
</template>
